// SourceTypeBadge — small typographic glyph + text label for one of the
// four KB entry types. One muted colour per type; no icons (per designer
// Q2). The label carries the meaning; the glyph is `aria-hidden` decoration.

import type { CoachCitation } from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import styles from './Coach.module.css';

export type SourceType = NonNullable<CoachCitation['type']>;

/** Glyph per type, from the designer strings spec. Decorative only. */
const GLYPH: Record<SourceType, string> = {
  qa_pair: '◆',
  framework: '▦',
  case_study: '◎',
  document_excerpt: '❡',
};

/** i18n key per type (resolved on the consumer side). */
const LABEL_KEY: Record<SourceType, string> = {
  qa_pair: 'sources.type.qa_pair',
  framework: 'sources.type.framework',
  case_study: 'sources.type.case_study',
  document_excerpt: 'sources.type.document_excerpt',
};

export interface SourceTypeBadgeProps {
  type: SourceType;
  /** Optional override: when the badge is a count ("3 case studies"), the
   *  parent passes the pluralized label; this lets us keep the component
   *  reusable without pluralization logic here. */
  label?: string;
}

export function SourceTypeBadge({ type, label }: SourceTypeBadgeProps) {
  const { t } = useI18n();
  const resolvedLabel = label ?? t(LABEL_KEY[type]);
  return (
    <span className={`${styles.srcTypeBadge} ${styles[`srcType_${type}`] ?? ''}`}>
      <span aria-hidden="true" className={styles.srcTypeGlyph}>
        {GLYPH[type]}
      </span>
      {resolvedLabel}
    </span>
  );
}
